import React from 'react';
import { Route, Redirect, Switch, Router } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import ProtectedRoute from '../routes';
import { appNamedRoutes } from '../routes';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import { parse, stringify } from 'query-string';

import * as features from '../utils/features';

import AdministrationFamiliesPage from 'components/administration/families/AdministrationFamiliesPage';
import AdministrationFamiliesEditPage from 'components/administration/families/AdministrationFamiliesEditPage';
import AdministrationFamiliesNewPage from 'components/administration/families/AdministrationFamiliesNewPage';

import AdministrationStaffPage from 'components/administration/staff/AdministrationStaffPage';
import AdministrationStaffEditPage from 'components/administration/staff/AdministrationStaffEditPage';
import AdministrationStaffNewPage from 'components/administration/staff/AdministrationStaffNewPage';

import AdministrationOrganizationPage from 'components/administration/organization/AdministrationOrganizationPage';

import SettingsPage from './SettingsPage';
import ASQSurveysPage from './ASQSurveysPage';
import ASQSurveyPage from './ASQSurveyPage';
import ASQPlayerPage from './ASQPlayerPage';
import DashboardPage from './DashboardPage';
import AccountsPage from './AccountsPage';
import AccountPage from './AccountPage';
import AccountPlayersPage from './AccountPlayersPage';
import AccountPlayerPage from './AccountPlayerPage';
import CardsLibraryPage from 'components/library/cards/CardsLibraryPage';
import ProgramContentLibraryPage from 'components/library/programContent/ProgramContentLibraryPage';
import GroupCohortsReportGeneralPage from './GroupCohortsReportGeneralPage';
import GroupCohortsReportOrganizationTypePage from './GroupCohortsReportOrganizationTypePage';
import Login from './Login';
import SingleSignOn from './SingleSignOn';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import NotFound from '../components/NotFound';
import ModalRoot from './modals/ModalRoot';
import ErrorBoundary from './ErrorBoundary';
import { ALL_DASHBOARD_SCOPE, TEACHER_DASHBOARD_SCOPE, PARTNER_DASHBOARD_SCOPE, TRANSLATOR_DASHBOARD_SCOPE } from '../utils/authScopes';
import Error from '../components/Error';
import CommunicationPage from "./CommunicationPage";

// eslint-disable-next-line react/prop-types
const Root = ({ store, history }) => (
  <ErrorBoundary>
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <Error />
        <Router history={history}>
          <QueryParamProvider
            adapter={ReactRouter5Adapter}
            options={{
              searchStringToObject: parse,
              objectToSearchString: stringify,
            }}
          >
            <Switch>
              <Route path={appNamedRoutes.login} exact component={Login} />
              <Route path={appNamedRoutes.sso} exact component={SingleSignOn} />
              <Route path={appNamedRoutes.forgotPassword} exact component={ForgotPasswordPage} />
              <Route path={appNamedRoutes.resetPassword} exact component={ResetPasswordPage} />
              <ProtectedRoute path={appNamedRoutes.root} exact component={NotFound} scope={{ roles: [], type: 'root' }} />
              <ProtectedRoute path={appNamedRoutes.asqs} exact component={ASQSurveysPage} scope={{ roles: TEACHER_DASHBOARD_SCOPE, type: 'asqs' }} />
              <ProtectedRoute path={appNamedRoutes.asq} exact component={ASQSurveyPage} scope={{ roles: TEACHER_DASHBOARD_SCOPE, type: 'asq' }} />
              <ProtectedRoute path={appNamedRoutes.player} exact component={ASQPlayerPage} scope={{ roles: TEACHER_DASHBOARD_SCOPE, type: 'asq' }} />
              <ProtectedRoute path={appNamedRoutes.partnerRoot} exact component={DashboardPage} scope={{ roles: PARTNER_DASHBOARD_SCOPE, type: 'partnerRoot' }} />
              <ProtectedRoute path={appNamedRoutes.accounts} exact component={AccountsPage} scope={{ roles: PARTNER_DASHBOARD_SCOPE, type: 'accounts' }} />
              <ProtectedRoute path={appNamedRoutes.account} exact component={AccountPage} scope={{ roles: PARTNER_DASHBOARD_SCOPE, type: 'account' }} />
              <ProtectedRoute path={appNamedRoutes.accountPlayers} exact component={AccountPlayersPage} scope={{ roles: PARTNER_DASHBOARD_SCOPE, type: 'accountPlayers' }} />
              <ProtectedRoute path={appNamedRoutes.accountPlayer} exact component={AccountPlayerPage} scope={{ roles: PARTNER_DASHBOARD_SCOPE, type: 'accountPlayer' }} />
              <ProtectedRoute path={appNamedRoutes.groupcohortsReportGeneral} exact component={GroupCohortsReportGeneralPage} scope={{ roles: PARTNER_DASHBOARD_SCOPE, type: 'groupcohortsReport' }} />
              <ProtectedRoute path={appNamedRoutes.groupcohortsReportOrganizationType} exact component={GroupCohortsReportOrganizationTypePage} scope={{ roles: PARTNER_DASHBOARD_SCOPE, type: 'groupcohortsReport' }} />
              <Route path={appNamedRoutes.groupcohortsReport} exact>
                <Redirect to={appNamedRoutes.groupcohortsReportGeneral} />
              </Route>

              <ProtectedRoute path={appNamedRoutes.cardsLibrary} exact component={CardsLibraryPage} scope={{ feature: features.CONTENT_LIBRARY_VIEW }} />
              <ProtectedRoute path={appNamedRoutes.programContentLibrary} exact component={ProgramContentLibraryPage} scope={{ feature: features.CONTENT_LIBRARY_VIEW }} />
              <Route path={appNamedRoutes.library} exact>
                <Redirect to={appNamedRoutes.cardsLibrary} />
              </Route>
              <ProtectedRoute partnerRequired path={appNamedRoutes.administrationStaff} exact component={AdministrationStaffPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'administration' }} />
              <ProtectedRoute partnerRequired path={appNamedRoutes.administrationStaffNew} exact component={AdministrationStaffNewPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'administration' }} />
              <ProtectedRoute partnerRequired path={appNamedRoutes.administrationStaffEdit} exact component={AdministrationStaffEditPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'administration' }} />
              <ProtectedRoute partnerRequired path={appNamedRoutes.administrationOrganization} exact component={AdministrationOrganizationPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'administration' }} />
              <ProtectedRoute partnerRequired path={appNamedRoutes.administrationFamiliesNew} exact component={AdministrationFamiliesNewPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'administration' }} />
              <ProtectedRoute partnerRequired path={appNamedRoutes.administrationFamiliesEdit} exact component={AdministrationFamiliesEditPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'administration' }} />
              <ProtectedRoute partnerRequired path={appNamedRoutes.administrationFamilies} exact component={AdministrationFamiliesPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'administration' }} />
              <Route path={appNamedRoutes.administration} exact>
                <Redirect to={appNamedRoutes.administrationStaff} />
              </Route>
              <ProtectedRoute path={appNamedRoutes.settings} exact component={SettingsPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'settings' }} />
              <ProtectedRoute path={appNamedRoutes.communicationIndividuals} exact component={CommunicationPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'communicationDashboard' }} />
              <ProtectedRoute path={appNamedRoutes.communicationGroups} exact component={CommunicationPage} scope={{ roles: ALL_DASHBOARD_SCOPE, type: 'communicationDashboard' }} />
              <Route path={appNamedRoutes.communicationDashboard} exact>
                <Redirect to={appNamedRoutes.communicationIndividuals} />
              </Route>
              <Route path={appNamedRoutes.notFound} exact component={NotFound} />
              {/*
                Escape hatch: the SPA is mounted only under /dashboard (see config/routes.rb).
                If client-side navigation lands on a non-dashboard path (e.g. /admin or a Devise
                page), bail out of React and let Rails serve that URL via a full page load
                instead of rendering NotFound.
              */}
              <Route path='*' render={({ location }) => {
                if (!location.pathname.startsWith('/dashboard')) {
                  window.location.replace(location.pathname + location.search + location.hash);
                  return null;
                }
                return <NotFound />;
              }} />
            </Switch>
          </QueryParamProvider>
          <ModalRoot />
        </Router>
      </ConnectedRouter>
    </Provider>
  </ErrorBoundary>
)

export default Root;
